import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import {
  SITE_URL,
  SITE_NAME,
  DEFAULT_SEO_DESCRIPTION,
  DEFAULT_SEO_IMAGE,
} from "../utils/constants";

// Turns HTML (e.g. a product description) into one line of plain text for a meta description.
export const toMetaDescription = (html, maxLength = 160) => {
  if (!html) return "";
  const text = new DOMParser()
    .parseFromString(html, "text/html")
    .body.textContent.replace(/\s+/g, " ")
    .trim();
  return text.length > maxLength ? `${text.slice(0, maxLength - 1).trimEnd()}…` : text;
};

// Title, description, canonical URL, social preview tags and structured data for one page.
// `path` overrides the canonical path (defaults to the current path without its query string);
// `noindex` keeps private or thin pages (cart, checkout, account, search) out of search results.
const Seo = ({
  title,
  description = DEFAULT_SEO_DESCRIPTION,
  path,
  image = DEFAULT_SEO_IMAGE,
  type = "website",
  noindex = false,
  jsonLd,
}) => {
  const location = useLocation();
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} | Online Shopping in Sri Lanka`;
  const url = `${SITE_URL}${path ?? location.pathname}`;
  const structuredData = [].concat(jsonLd || []);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={noindex ? "noindex, follow" : "index, follow"} />
      {!noindex && <link rel="canonical" href={url} />}

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {structuredData.map((data, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;
